import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { format } from 'date-fns';

import { AgaveDataGrid } from '@/components/agave-data-grid';
import Breadcrumb from '@/components/breadcrumb';
import {
  canDownloadShippingReceipt,
  formatOrderNumberDisplay,
  getShippingReceiptDisabledTooltip,
} from '@/utils/shipping-receipt';
import { formatFulfillmentStatus } from '@/utils/fulfillment-status';

import type { IzziSaleOrder } from '@/domain/izzi';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { useOrdersSection } from './use-orders-section';

const formatDate = (value: unknown) =>
  value ? format(new Date(value as string), 'dd/MM/yyyy HH:mm') : '—';

const getFulfillmentChipColor = (status?: string): 'success' | 'default' | 'warning' => {
  if (status?.toLowerCase() === 'fulfilled') {
    return 'success';
  }

  if (status?.toLowerCase() === 'processing') {
    return 'warning';
  }

  return 'default';
};

export default function OrdersSection() {
  const hooks = useOrdersSection();

  const columns: GridColDef<IzziSaleOrder>[] = [
    { field: 'customerSo', headerName: 'Customer SO', flex: 1, minWidth: 140 },
    {
      field: 'orderNumber',
      headerName: 'OrderNumber',
      width: 150,
      valueGetter: (_value, row) => formatOrderNumberDisplay(row),
    },
    { field: 'trackingId', headerName: 'Tracking', flex: 1, minWidth: 160 },
    {
      field: 'fulfillmentStatus',
      headerName: 'Estado Fulfillment',
      width: 170,
      renderCell: ({ value }: GridRenderCellParams<IzziSaleOrder, string>) => (
        <Chip
          label={formatFulfillmentStatus(value)}
          size="small"
          color={getFulfillmentChipColor(value)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de creación',
      width: 170,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 220,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }: GridRenderCellParams<IzziSaleOrder>) => {
        const isDownloading = hooks.downloadingOrderId === row.id;
        const canDownload = canDownloadShippingReceipt(row);
        const disabledTooltip = getShippingReceiptDisabledTooltip(row);

        return (
          <Tooltip title={canDownload ? 'Descargar acuse de envío (PDF)' : disabledTooltip}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                disabled={!canDownload || isDownloading || hooks.isLoading}
                onClick={() => hooks.onDownloadShippingReceipt(row)}
              >
                Descargar acuse
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="p-6 flex flex-col gap-4">
      <Breadcrumb
        sections={[{ label: 'Izzi' }, { label: 'Órdenes de venta' }]}
        description="Consulta órdenes de venta y descarga el acuse de envío cuando la orden tenga series completas"
        actions={
          <Tooltip title="Actualizar datos">
            <span>
              <IconButton onClick={hooks.fetchOrders} disabled={hooks.isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Card variant="outlined">
        <CardContent>
          <div className="flex gap-3 mb-4 items-center flex-wrap">
            <TextField
              label="Customer SO"
              variant="filled"
              size="small"
              value={hooks.searchCustomerSo}
              onChange={(event) => hooks.onSearchCustomerSoChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  hooks.onSearch();
                }
              }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="OrderNumber"
              variant="filled"
              size="small"
              value={hooks.searchOrderNumber}
              onChange={(event) => hooks.onSearchOrderNumberChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  hooks.onSearch();
                }
              }}
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" onClick={hooks.onSearch} startIcon={<SearchIcon />}>
              Buscar
            </Button>
            {hooks.hasAppliedFilters && (
              <>
                <Button variant="outlined" color="inherit" onClick={hooks.onClearSearch}>
                  Limpiar filtros
                </Button>
                {hooks.appliedCustomerSo && (
                  <Chip
                    label={`Customer SO: ${hooks.appliedCustomerSo}`}
                    size="small"
                    onDelete={hooks.onClearSearch}
                  />
                )}
                {hooks.appliedOrderNumber && (
                  <Chip
                    label={`OrderNumber: ${hooks.appliedOrderNumber}`}
                    size="small"
                    onDelete={hooks.onClearSearch}
                  />
                )}
              </>
            )}
          </div>

          <AgaveDataGrid
            rows={hooks.data.data}
            columns={columns}
            getRowId={(row) => row.id}
            rowHeight={56}
            paginationMode="server"
            rowCount={hooks.data.total}
            paginationModel={hooks.paginationModel}
            onPaginationModelChange={hooks.onPaginationModelChange}
            pageSizeOptions={[10, 20, 50]}
            loading={hooks.isLoading}
            sx={{ height: 520 }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
